(() => {
  const root = document.querySelector('[data-led-landing]');
  if (!root) return;

  const shippingCode = root.dataset.ledShippingCode?.trim();
  const shippingStorageKey = 'wellness-route-led-flex-shipping-unlocked';
  let shippingUnlocked = false;

  try {
    shippingUnlocked = window.localStorage.getItem(shippingStorageKey) === 'true';
  } catch (error) {
    shippingUnlocked = false;
  }

  const unlockForm = root.querySelector('.led-shipping-unlock-form');
  const unlockSuccess = root.querySelector('[data-led-shipping-unlock-success]');
  const unlockError = root.querySelector('[data-led-shipping-unlock-error]');
  const unlockSubmit = root.querySelector('[data-led-shipping-unlock-submit]');
  const copyShippingCode = root.querySelector('[data-led-copy-shipping-code]');
  const unlockModal = root.querySelector('[data-led-shipping-modal]');
  const unlockDialog = root.querySelector('[data-led-shipping-modal-dialog]');
  const unlockTriggers = [...root.querySelectorAll('[data-led-open-shipping-modal]')];
  const unlockCloseButtons = [...root.querySelectorAll('[data-led-close-shipping-modal]')];
  const unlockLockedSummaries = [...root.querySelectorAll('[data-led-shipping-locked-summary]')];
  const unlockUnlockedSummaries = [...root.querySelectorAll('[data-led-shipping-unlocked-summary]')];
  const unlockTriggerLabels = [...root.querySelectorAll('[data-led-shipping-trigger-label]')];
  const unlockModalTitle = root.querySelector('[data-led-shipping-modal-title]');
  const unlockModalCopy = root.querySelector('[data-led-shipping-modal-copy]');
  const heroDefaultLabel = root.querySelector('.led-buy-form--hero .led-cta-label--default');
  let unlockModalLastFocus = null;

  if (unlockModal && unlockModal.parentElement !== document.body) {
    document.body.appendChild(unlockModal);
  }

  const setShippingModalOpen = (shouldOpen) => {
    if (!unlockModal || !unlockDialog) return;
    unlockModal.hidden = !shouldOpen;
    unlockModal.setAttribute('aria-hidden', shouldOpen ? 'false' : 'true');
    root.classList.toggle('is-shipping-modal-open', shouldOpen);
    document.body.classList.toggle('led-shipping-modal-open', shouldOpen);
    unlockTriggers.forEach((trigger) => trigger.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false'));

    if (shouldOpen) {
      unlockModalLastFocus = document.activeElement;
      window.requestAnimationFrame(() => {
        const focusTarget = shippingUnlocked
          ? copyShippingCode
          : unlockForm?.querySelector('input[name="contact[email]"]');
        (focusTarget || unlockDialog).focus();
      });
    } else if (unlockModalLastFocus instanceof HTMLElement) {
      unlockModalLastFocus.focus();
    }
  };

  unlockTriggers.forEach((trigger) => {
    trigger.addEventListener('click', () => setShippingModalOpen(true));
  });

  unlockCloseButtons.forEach((button) => {
    button.addEventListener('click', () => setShippingModalOpen(false));
  });

  document.addEventListener('keydown', (event) => {
    if (!unlockModal || unlockModal.hidden) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      setShippingModalOpen(false);
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = [...unlockModal.querySelectorAll('button:not([disabled]), input:not([disabled]), a[href]')]
      .filter((element) => !element.hidden && element.getClientRects().length > 0);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  const updateShippingUnlock = () => {
    root.classList.toggle('is-shipping-unlocked', shippingUnlocked);
    if (unlockForm) unlockForm.hidden = shippingUnlocked;
    if (unlockSuccess) unlockSuccess.hidden = !shippingUnlocked;
    unlockLockedSummaries.forEach((summary) => { summary.hidden = shippingUnlocked; });
    unlockUnlockedSummaries.forEach((summary) => { summary.hidden = !shippingUnlocked; });
    unlockTriggerLabels.forEach((label) => {
      label.textContent = shippingUnlocked ? 'View shipping code' : 'Unlock free shipping';
    });
    if (unlockModalTitle) {
      unlockModalTitle.textContent = shippingUnlocked
        ? 'Free shipping is unlocked'
        : 'Unlock free shipping on your LED FLEX Mask';
    }
    if (unlockModalCopy) {
      unlockModalCopy.textContent = shippingUnlocked
        ? 'Your private code is ready and will be applied when you add LED FLEX to cart.'
        : 'Join our LED FLEX email series and reveal your private shipping code.';
    }
    if (heroDefaultLabel) {
      heroDefaultLabel.textContent = shippingUnlocked
        ? root.dataset.ledHeroButtonUnlocked
        : root.dataset.ledHeroButtonLocked;
    }
  };

  const setShippingUnlocked = () => {
    shippingUnlocked = true;
    try {
      window.localStorage.setItem(shippingStorageKey, 'true');
    } catch (error) {
      // The current page session still retains the unlocked state.
    }
    updateShippingUnlock();
  };

  const unlockFromRedirect = new URLSearchParams(window.location.search).get('shipping_unlocked') === '1';
  if (unlockFromRedirect) {
    setShippingUnlocked();
    const cleanUrl = new URL(window.location.href);
    cleanUrl.searchParams.delete('shipping_unlocked');
    window.history.replaceState({}, '', `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`);
    setShippingModalOpen(true);
  } else {
    updateShippingUnlock();
  }

  unlockForm?.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!unlockForm.reportValidity()) return;
    if (unlockError) unlockError.hidden = true;

    const originalLabel = unlockSubmit?.textContent;
    if (unlockSubmit) {
      unlockSubmit.disabled = true;
      unlockSubmit.textContent = 'Unlocking...';
    }

    try {
      const response = await fetch(unlockForm.action, {
        method: 'POST',
        body: new FormData(unlockForm),
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) throw new Error('Unable to subscribe.');
      setShippingUnlocked();
      window.requestAnimationFrame(() => copyShippingCode?.focus());
      window.dataLayer?.push({
        event: 'led_shipping_unlocked',
        offer: 'LED FLEX free shipping',
      });
    } catch (error) {
      if (unlockError) unlockError.hidden = false;
    } finally {
      if (unlockSubmit) {
        unlockSubmit.disabled = false;
        unlockSubmit.textContent = originalLabel;
      }
    }
  });

  copyShippingCode?.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(shippingCode);
      copyShippingCode.textContent = 'Code copied';
    } catch (error) {
      copyShippingCode.textContent = shippingCode;
    }
  });

  if (shippingCode) {
    root.querySelectorAll('.led-buy-form').forEach((form) => {
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const button = form.querySelector('button[type="submit"]');
        const buttonLabels = button ? [...button.querySelectorAll('.led-cta-label')] : [];
        const originalLabels = buttonLabels.map((label) => label.textContent);
        const originalLabel = button?.textContent;
        const setButtonLabel = (label) => {
          if (buttonLabels.length) {
            buttonLabels.forEach((buttonLabel) => { buttonLabel.textContent = label; });
          } else if (button) {
            button.textContent = label;
          }
        };
        const variantId = form.querySelector('input[name="id"]')?.value;
        const quantity = Number(form.querySelector('input[name="quantity"]')?.value || 1);
        const rootPath = window.Shopify?.routes?.root || '/';
        let itemAdded = false;

        if (button) {
          button.disabled = true;
          setButtonLabel('Adding to cart...');
        }
        root.classList.add('is-purchasing');

        try {
          const addResponse = await fetch(`${rootPath}cart/add.js`, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              items: [{
                id: variantId,
                quantity,
                properties: shippingUnlocked ? { _led_shipping_unlocked: 'true' } : {},
              }],
            }),
          });
          if (!addResponse.ok) throw new Error('Unable to add the mask to cart.');
          itemAdded = true;

          if (shippingUnlocked && shippingCode) {
            const discountResponse = await fetch(`${rootPath}cart/update.js`, {
              method: 'POST',
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ discount: shippingCode }),
            });
            if (!discountResponse.ok) throw new Error('Unable to apply your free shipping.');
          }

          window.location.assign(`${rootPath}cart`);
        } catch (error) {
          if (button) {
            button.disabled = false;
            setButtonLabel(itemAdded ? 'View cart' : 'Please try again');
          }
          if (itemAdded) window.location.assign(`${rootPath}cart`);
        } finally {
          if (button && document.visibilityState === 'visible') {
            window.setTimeout(() => {
              button.disabled = false;
              if (buttonLabels.length) {
                buttonLabels.forEach((buttonLabel, index) => {
                  buttonLabel.textContent = originalLabels[index] || originalLabel;
                });
              } else {
                button.textContent = originalLabel;
              }
              root.classList.remove('is-purchasing');
              window.dispatchEvent(new Event('led:purchase-state-change'));
            }, 2500);
          } else {
            root.classList.remove('is-purchasing');
          }
        }
      });
    });
  }

  const mobileBuy = root.querySelector('[data-led-mobile-buy]');
  const primaryHeroForm = root.querySelector('.led-buy-form--hero');
  const primaryHeroButton = primaryHeroForm?.querySelector('.led-cta');

  if (mobileBuy && primaryHeroButton) {
    const cartIsOpen = () => document.body.classList.contains('cart-drawer-open')
      || document.body.classList.contains('drawer-open')
      || Boolean(document.querySelector('cart-drawer[open], .cart-drawer.is-open, [data-cart-drawer][aria-hidden="false"]'));

    const syncMobileBuy = () => {
      const hasPassedPrimaryCta = primaryHeroButton.getBoundingClientRect().bottom <= 0;
      const shouldShow = window.matchMedia('(max-width: 600px)').matches
        && hasPassedPrimaryCta
        && !root.classList.contains('is-purchasing')
        && !cartIsOpen();

      mobileBuy.classList.toggle('is-visible', shouldShow);
      mobileBuy.setAttribute('aria-hidden', String(!shouldShow));
      mobileBuy.inert = !shouldShow;
    };

    const primaryCtaObserver = new IntersectionObserver(syncMobileBuy, { threshold: [0, 1] });
    primaryCtaObserver.observe(primaryHeroButton);
    window.addEventListener('scroll', syncMobileBuy, { passive: true });
    window.addEventListener('resize', syncMobileBuy, { passive: true });
    window.addEventListener('led:purchase-state-change', syncMobileBuy);
    new MutationObserver(syncMobileBuy).observe(document.body, { attributes: true, attributeFilter: ['class'] });
    syncMobileBuy();
  }

  const gallery = root.querySelector('[data-led-gallery]');
  if (gallery) {
    const image = gallery.querySelector('[data-led-gallery-image]');
    const thumbs = [...gallery.querySelectorAll('[data-led-gallery-thumb]')];
    const count = gallery.querySelector('[data-led-gallery-count]');
    const main = gallery.querySelector('.led-gallery-main');
    let activeIndex = 0;
    let touchStartX = 0;

    const selectImage = (nextIndex) => {
      if (!image || !thumbs.length) return;
      activeIndex = (nextIndex + thumbs.length) % thumbs.length;
      const thumb = thumbs[activeIndex];
      image.classList.add('is-changing');
      window.setTimeout(() => {
        image.src = thumb.dataset.src;
        image.srcset = thumb.dataset.srcset;
        image.alt = thumb.dataset.alt || 'LED FLEX Mask';
        image.style.objectPosition = thumb.dataset.position || 'center';
        image.style.objectFit = thumb.dataset.fit || 'cover';
        image.classList.remove('is-changing');
      }, 100);

      thumbs.forEach((item, index) => {
        const isActive = index === activeIndex;
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-pressed', String(isActive));
      });
      thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      if (count) count.textContent = `${activeIndex + 1} / ${thumbs.length}`;
    };

    thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => selectImage(index)));
    gallery.querySelector('[data-led-gallery-prev]')?.addEventListener('click', () => selectImage(activeIndex - 1));
    gallery.querySelector('[data-led-gallery-next]')?.addEventListener('click', () => selectImage(activeIndex + 1));

    main?.addEventListener('touchstart', (event) => {
      touchStartX = event.changedTouches[0]?.clientX || 0;
    }, { passive: true });
    main?.addEventListener('touchend', (event) => {
      const touchEndX = event.changedTouches[0]?.clientX || 0;
      const distance = touchEndX - touchStartX;
      if (Math.abs(distance) < 45) return;
      selectImage(activeIndex + (distance < 0 ? 1 : -1));
    }, { passive: true });
  }

  const lightSelector = root.querySelector('[data-led-light-selector]');
  if (lightSelector) {
    const buttons = [...lightSelector.querySelectorAll('[data-light-name]')];
    const name = lightSelector.querySelector('[data-led-light-name]');
    const wavelength = lightSelector.querySelector('[data-led-light-nm]');
    const title = lightSelector.querySelector('[data-led-light-title]');
    const copy = lightSelector.querySelector('[data-led-light-copy]');
    const goals = lightSelector.querySelector('[data-led-light-goals]');
    const display = lightSelector.querySelector('.led-light-display');

    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        buttons.forEach((item) => {
          const isActive = item === button;
          item.classList.toggle('is-active', isActive);
          item.setAttribute('aria-pressed', String(isActive));
        });
        lightSelector.style.setProperty('--led-glow', button.dataset.lightColor);
        name.textContent = button.dataset.lightName;
        wavelength.textContent = button.dataset.lightNm;
        title.textContent = button.dataset.lightTitle;
        copy.textContent = button.dataset.lightCopy;
        goals.textContent = button.dataset.lightGoals;
      });
    });

    lightSelector.querySelectorAll('[data-led-match-light]').forEach((match) => {
      match.addEventListener('click', () => {
        const button = buttons.find((item) => item.dataset.lightName === match.dataset.ledMatchLight);
        if (!button) return;
        button.click();
        display?.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'center'
        });
      });
    });
  }

  root.querySelectorAll('[data-led-moments]').forEach((moments) => {
    const track = moments.querySelector('[data-led-moments-track]');
    const previous = moments.querySelector('[data-led-moments-prev]');
    const next = moments.querySelector('[data-led-moments-next]');
    const status = moments.querySelector('[data-led-moments-status]');

    if (track && previous && next) {
      const cards = Array.from(track.querySelectorAll('.led-moment-card'));
      const scrollBehavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      const getScrollStep = () => {
        const firstCard = cards[0];
        const styles = window.getComputedStyle(track);
        const gap = parseFloat(styles.columnGap || styles.gap) || 0;
        return (firstCard?.getBoundingClientRect().width || track.clientWidth) + gap;
      };
      const updateControls = () => {
        const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
        const step = getScrollStep();
        const current = Math.min(cards.length, Math.max(1, Math.round(track.scrollLeft / step) + 1));
        previous.disabled = track.scrollLeft <= 8;
        next.disabled = track.scrollLeft >= maxScroll - 8;
        if (status) status.textContent = `${String(current).padStart(2, '0')} / ${String(cards.length).padStart(2, '0')}`;
      };

      previous.addEventListener('click', () => track.scrollBy({ left: -getScrollStep(), behavior: scrollBehavior }));
      next.addEventListener('click', () => track.scrollBy({ left: getScrollStep(), behavior: scrollBehavior }));
      track.addEventListener('scroll', updateControls, { passive: true });
      window.addEventListener('resize', updateControls, { passive: true });
      window.requestAnimationFrame(updateControls);
    }
  });

  root.querySelectorAll('[data-led-accordion-group]').forEach((group) => {
    group.querySelectorAll('details').forEach((details) => {
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        group.querySelectorAll('details[open]').forEach((openDetails) => {
          if (openDetails !== details) openDetails.open = false;
        });
      });
    });
  });

  const faq = root.querySelector('[data-led-faq]');
  if (faq) {
    faq.querySelectorAll('details').forEach((details) => {
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        faq.querySelectorAll('details[open]').forEach((openDetails) => {
          if (openDetails !== details) openDetails.open = false;
        });
      });
    });
  }
})();

